import apiClient from './api';

/**
 * DRI-95 report view loaders: the served report HTML for the staff preview and the portal report view, rendered by
 * `ReportGraphsView`. Same transport as the share viewer's `fetchSectionHtml` (text, 300 s, as the nginx blocks).
 *
 * Contract (`ReportGraphsView`, handoff D19): 2xx → the HTML; 404 → null ("not available yet"); any other status →
 * `throw new Error(message)` with the server's `ApiResponse` message (the staff 409 "recalculate" text, the portal 403
 * gate text, the 429 render-busy copy), or the generic fallback when the body does not parse. A 401 still goes through
 * the interceptor (token refresh, then a retry).
 */

/** Shown when an error body carries no readable `message`. Same words as `ReportGraphsView`'s fallback. */
export const REPORT_VIEW_LOAD_FAILED_MESSAGE = 'The report could not be loaded.';

/** Client timeout of a report view load: the server may render a large batch cold (nginx allows 300 s). */
export const REPORT_VIEW_TIMEOUT_MS = 300000;

/** The `message` of an error body (a JSON string with `responseType: 'text'`, or an already-parsed object). */
export const readReportViewErrorMessage = (data: unknown): string => {
  let body: unknown = data;
  if (typeof data === 'string') {
    try {
      body = JSON.parse(data);
    } catch {
      return REPORT_VIEW_LOAD_FAILED_MESSAGE;
    }
  }
  const message = (body as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message.trim() ? message : REPORT_VIEW_LOAD_FAILED_MESSAGE;
};

/** GET a report view as HTML under the contract above. */
export const fetchReportViewHtml = async (url: string, signal?: AbortSignal): Promise<string | null> => {
  const response = await apiClient.get(url, {
    responseType: 'text',
    timeout: REPORT_VIEW_TIMEOUT_MS,
    signal,
    // Every status but 401 resolves here, so the body is read below rather than by the interceptor, which would
    // keep only an `errorCode` envelope and turn the rest into a bare axios message.
    validateStatus: (status: number) => status !== 401,
  });
  if (response.status >= 200 && response.status < 300) return response.data as string;
  if (response.status === 404) return null;
  throw new Error(readReportViewErrorMessage(response.data));
};

export const reportViewService = {
  /**
   * Staff preview of a batch's report: the share Analysis HTML ShareLink viewers get
   * (`GET api/report-view/batches/{id}/analysis`). 409 = the soil analysis is not Ready (staff text).
   */
  getStaffAnalysis: (batchId: number, signal?: AbortSignal): Promise<string | null> =>
    fetchReportViewHtml(`/report-view/batches/${batchId}/analysis`, signal),
};

export default reportViewService;
